import React, { useState, useEffect, useRef } from 'react';
import { Autocomplete, Box, Collapse, Link, TextField, Typography } from '@mui/material';

import { searchAddress, searchUspsAddress, retrieveUspsAddress } from '../api/Store';
import { useCustomerReservation } from '../common/Providers/CustomerReservationProvider/UseCustomerReservation';
import { useStoreDetails } from '../common/Providers/StoreDetailsProvider/UseStoreDetails';
import { useManageKeys } from '../common/Providers/ManageKeysProvider/UseManageKeys';
import CustomBorderInput from '../common/CustomBorderInput';
import { useResponsiveValues } from '../common/Providers/DimentionsProvider/UseResponsiveValues';

interface props {
  sx?: object;
  isDescription?: boolean;
  isShowAddress?: boolean;
  isShowSearchBox?: boolean;
  contentStyle?: object;
  emptyError?: boolean;
}

const buildManualAddress = (base: string, unit: string) => {
  const trimmed = unit.trim();
  if (!trimmed) return base;
  return `${base}, Unit ${trimmed}`;
};

const DeliveryLocation: React.FC<props> = ({sx, isDescription, isShowAddress, isShowSearchBox, contentStyle, emptyError}) => {
  const { ReservationMain, setReservationValue } = useCustomerReservation();
  const { storeDetails } = useStoreDetails();
  const { getKey } = useManageKeys();
  const { matches900 } = useResponsiveValues();

  const [expandSearchBox, setExpandSearchBox] = useState<boolean>(false);
  const [searchKey, setSearchKey] = useState<string>("");
  const [searchedAddresses, setSearchedAddresses] = useState<Array<any>>([]);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [addressError, setAddressError] = useState(emptyError);

  // USPS Address Lock-in: when the brand requires it, the delivery address must
  // be chosen from a live typeahead of verified US addresses (no free text).
  const requireUsps = !!storeDetails.require_usps_address;

  useEffect(()=>{
    setAddressError(emptyError)
  }, [emptyError]);

  useEffect(()=>{
    if(isShowSearchBox) setExpandSearchBox(true);
    else setExpandSearchBox(false);
  }, [isShowSearchBox]);

  useEffect(() => {
    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchKey) {
      // Set a new timeout to delay the API call
      searchTimeoutRef.current = setTimeout(() => {
        if (requireUsps) {
          // Verified US-address typeahead (Mapbox-backed, proxied server-side).
          searchUspsAddress(searchKey, (jsonRes: any, status: any) => {
            if (status === 200 && Array.isArray(jsonRes)) {
              setSearchedAddresses(
                jsonRes.map((address, index) => ({
                  ...address,
                  label: `${address.full_address || ''}<span style="display:none;">${index}</span>`
                }))
              );
            } else {
              setSearchedAddresses([]);
            }
          }, storeDetails.usps_zip_codes || undefined);
          return;
        }

        searchAddress(searchKey, storeDetails.id, (jsonRes:any, status) => {
          if (status === 200 && Array.isArray(jsonRes)) {
            let filteredAddresses = jsonRes.map((address, index) => {
              const label = getAddressLabel(address, index)
              if(label === '') return null;
              return { ...address, label: label }
            })

            filteredAddresses = filteredAddresses.filter((item) => item)
            setSearchedAddresses(filteredAddresses);
          } else {
            setSearchedAddresses([]);
          }
        });
      }, 300); // 300ms delay
    } else {
      setSearchedAddresses([]);
    }

    // Cleanup function to clear timeout when component unmounts or effect reruns
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchKey, requireUsps, storeDetails.id, storeDetails.usps_zip_codes]);

  const handleUnitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setUnitNumber(value);
    if (ReservationMain.usps_address) {
      setReservationValue('manual_address', buildManualAddress(ReservationMain.usps_address, value));
    }
  };

  const handleUspsSelect = async (value: any) => {
    if (!value) {
      setReservationValue('selectedAddress', null);
      setReservationValue('address_id', null);
      setReservationValue('usps_address', '');
      setReservationValue('mapbox_id', null);
      setReservationValue('manual_address', '');
      setReservationValue('lat', null);
      setReservationValue('lng', null);
      setUnitNumber('');
      return;
    }

    setAddressError(false);
    const base = value.full_address || '';
    const manual = buildManualAddress(base, unitNumber);

    setReservationValue('selectedAddress', value);
    setReservationValue('address_id', null);
    setReservationValue('use_manual', true);
    setReservationValue('usps_address', base);
    setReservationValue('mapbox_id', value.mapbox_id || null);
    setReservationValue('manual_address', manual);

    if (value.mapbox_id) {
      try {
        const res: any = await retrieveUspsAddress(value.mapbox_id);
        if (res && res.ok) {
          const data = await res.clone().json();
          if (data && data.mapbox_id) {
            setReservationValue('lat', data.latitude ?? null);
            setReservationValue('lng', data.longitude ?? null);
          }
        }
      } catch (err) {
        console.error('[DeliveryLocation] retrieveUspsAddress failed:', err);
      }
    }
  };

  const getAddressLabel = (address: any, index: number) => {
    if (address.property_type === null || address.property_type === undefined || address.property_type === '') {
      return '';
    }
    
    switch (address.property_type.toString()) {
      case '0':
        return `${address.number || ''} ${address.street || ''}, ${address.plantation || ''}${address.property_name? ` - ${address.property_name}` :''}<span style="display:none;">${index}</span>`;
      case '1':
        return `${address.number || ''} ${address.street === '' || address.street === null ? address.property_name || '' : address.street || ''}, ${address.plantation || ''}<span style="display:none;">${index}</span>`;
      default:
        return '';
    }
  };

  const [unitNumber, setUnitNumber] = useState<string>("");

// console.log(ReservationMain.address_id);
  return (
    <Box sx={sx}>
      <Typography sx={{textDecoration:'underline', fontSize:'20px', marginBottom:'6px'}}>{`Vacation Address`}</Typography>
      <Box sx={contentStyle}>
        {isDescription && !requireUsps && <Typography dangerouslySetInnerHTML={{ __html: getKey('checkout_vacation_address_description', `We have a robust database of locations on the island. Search for a location and select the appropriate address from the dropdown. If your address is not listed, click below to enter your address manually. Please search for your address first.`) }} />}
        {isShowAddress && 
          <Typography style={{marginTop: '20px', fontWeight: 700, fontSize: '20px'}}>
          {ReservationMain.use_manual ? 
            (ReservationMain.manual_address || ' ') :
            (ReservationMain.selectedAddress && 
              `${ReservationMain.selectedAddress.number ?? ''} ${ReservationMain.selectedAddress.street ?? ''}${ReservationMain.selectedAddress.property_name ? `${ReservationMain.selectedAddress.property_name}` :  ''}, ${ReservationMain.selectedAddress.plantation ?? ''}`
            )
          }
        </Typography>
        }
        {isShowSearchBox === false && (
          <Box 
            sx={{mt:'10px', fontSize:'18px', }}
          >
            <Link 
              onClick={()=>{setExpandSearchBox(!expandSearchBox)}}
              sx={{cursor:'pointer'}}
            >
              {`Change delivery address. `}
            </Link>
          </Box>
        )}

        <Collapse in={expandSearchBox}>
          <Typography style={{marginTop:'10px', fontSize:'15px'}}>{requireUsps ? 'Search Verified Address' : 'Search Address'}</Typography>
          <Autocomplete
            // Multiple={true}
            freeSolo
            sx={{ 
              width: matches900?'60%':'100%', 
              boxShadow: '2px 2px 6px #b3b3b3', 
              border: addressError === true ? '1px solid #f7776e' : '1px solid transparent',
              borderRadius: '4px',
            }}
            disableClearable
            options={searchedAddresses}
            value={ReservationMain.selectedAddress}
            onChange={(event, value)=>{
              if (requireUsps) {
                handleUspsSelect(value);
              } else {
                console.log(value);
                setAddressError(false);
                setReservationValue('selectedAddress', value);
                setReservationValue('address_id', value?.id || null);
              }
            }}
            // inputValue={searchKey}
            onInputChange={(event, value)=>{
              setSearchKey(value)
              if(!value){
                if (requireUsps) {
                  handleUspsSelect(null);
                } else {
                  setReservationValue('selectedAddress', null);
                  setReservationValue('address_id', null);
                }
              }
            }}
            onClose = {(event: React.SyntheticEvent, reason: string)=>{
              
            }}
            filterOptions={(x) => {
              return x;
            }}
            renderInput={(params) => {
              const text = String(params?.inputProps?.value ?? ' ');
              const spanIndex = text.indexOf('<span');
              const inputVal = spanIndex !== -1 ? text.substring(0, spanIndex) : text;
              return (
                <TextField
                  {...params}
                  inputProps={{
                    ...params.inputProps,
                    type: 'search',
                    value: inputVal,
                  }}
                />
              )
            }}
            renderOption={(props, option) => {
              const htmlLabel = { __html: option.label };
              return (
                <li {...props}>
                  <span dangerouslySetInnerHTML={htmlLabel} />
                </li>
              );
            }}
          />

          {requireUsps && ReservationMain.selectedAddress && (
            <TextField
              label="Apt / Unit / Condo # (optional)"
              placeholder="e.g. Villa 1201"
              value={unitNumber}
              onChange={handleUnitChange}
              fullWidth
              sx={{ mt: 2, width: matches900 ? '60%' : '100%' }}
              size="small"
            />
          )}

          {!requireUsps && (
            <Box 
              sx={{mt:'10px', fontSize:'18px', }}
            >
              <Link 
                onClick={()=>{setReservationValue('use_manual', !ReservationMain.use_manual)}}
                sx={{cursor:'pointer'}}
              >
                {`Address not listed? Manually enter address.`}
              </Link>
            </Box>
          )}
        </Collapse>
        <Collapse in={expandSearchBox && !requireUsps && ReservationMain.use_manual}>
          <CustomBorderInput
            containerstyle={{ width: matches900?'60%':'100%', mt:'20px' }}
            label="Enter Address"
            placeholder="Resort, Street Address, Apt #/Suite/Etc." 
            value={ReservationMain.manual_address} 
            required={true}
            onChange={(event)=>{setReservationValue('manual_address', event.target.value)}} />
        </Collapse>
      </Box>
    </Box>
  );
}

export default DeliveryLocation;
